<script setup lang="ts">
import { useDeviceState } from "@/stores/DeviceState";
import { ref, computed } from "vue";
import DRDialog from "@/components/UI/feedbacks/DRDialog.vue";
import { useToast } from "@/composable/useToast";
import DRStatusBadge from "@/components/UI/DRStatusBadge.vue";
import type { DRLocalDrawingMeta, DRDrawing } from "@/types";
import { isDrawingSynced } from "@/utils/drawings.utils";
import { useDates } from "@/composable/useDates";

const props = defineProps<{
  localDrawing?: DRLocalDrawingMeta;
  drawing?: DRDrawing;
  spinning: boolean;
}>();

/**
 * Check if the drawing is using legacy VSFX format (not available offline).
 * Only DWG format revisions can be downloaded for offline use.
 */
const isLegacyFormat = computed(() => {
  const format = props.drawing?.lastRevision?.format;
  return format === "VSFX" || format === "VSF";
});

const { showWarning } = useToast();
const { formatDistanceToNow } = useDates();

const deviceState = useDeviceState();

const emits = defineEmits<{
  (e: "make-local"): void;
  (e: "retry-sync"): void;
}>();

const showError = ref(false);
const showRoughVersion = ref(false);

const onMakeLocal = () => {
  if (props.spinning) return;
  if (deviceState.isOnline) {
    emits("make-local");
  } else {
    showWarning("toast:drawing.online-required");
  }
};

const onRetry = () => {
  if (deviceState.isOnline) {
    emits("retry-sync");
    showError.value = false;
  }
};
</script>

<template>
  <div
    v-if="localDrawing"
    @click.prevent.stop
    class="d-flex gap-2"
    style="cursor: default"
  >
    <DRStatusBadge
      icon="bi-database-check"
      type="success"
      :label="$t('available-offline')"
      :tooltip="$t('available-offline-tooltip')"
    />

    <DRStatusBadge
      v-if="!!localDrawing.error"
      icon="bi-exclamation-circle-fill"
      type="danger"
      :label="$t('sync-failed')"
      clickable
      :tooltip="localDrawing.error"
      @select="showError = true"
    />

    <DRStatusBadge
      v-else-if="!isDrawingSynced(localDrawing)"
      icon="bi-floppy"
      type="warning"
      :label="$t('rough-version')"
      :tooltip="$t('rough-version-tooltip')"
      clickable
      @select="showRoughVersion = true"
    />
  </div>

  <div v-else @click.prevent.stop>
    <!-- Legacy VSFX format: not available for offline download -->
    <DRStatusBadge
      v-if="isLegacyFormat"
      icon="bi-archive"
      type="grey"
      :label="$t('legacy-format')"
      :tooltip="$t('legacy-format-tooltip')"
    />
    <!-- DWG format: can be downloaded for offline use -->
    <DRStatusBadge
      v-else
      @select="onMakeLocal"
      icon="bi-cloud-download"
      type="grey"
      clickable
      :loading="spinning"
      :label="$t('online-only')"
      :tooltip="$t('online-only-tooltip')"
    />
  </div>

  <DRDialog
    @click.stop.prevent
    v-model="showError"
    :title="$t('sync-failed')"
    @confirm="onRetry"
    @cancel="showError = false"
    :confirm-label="$t('retry')"
  >
    <div class="mb-2">
      {{ $t("dashboard:sync.error.modal.message1") }}
    </div>
    <div class="mb-2">
      {{ $t("dashboard:sync.error.modal.message2") }}
    </div>
    <div class="error-message">
      {{ $t("details") }} : <br />{{ localDrawing?.error }}
    </div>
  </DRDialog>

  <DRDialog
    @click.stop.prevent
    v-model="showRoughVersion"
    :title="$t('rough-version')"
    @cancel="showRoughVersion = false"
    :cancel-label="$t('understood')"
    :show-confirm="false"
  >
    <div class="mb-3">
      {{ $t("dashboard:rough-version.modal.message1") }}
    </div>
    <div class="mb-3">
      {{ $t("dashboard:rough-version.modal.message2") }}
    </div>
    <div class="mb-3">
      {{ $t("dashboard:rough-version.modal.message3") }}
    </div>
    <div v-if="localDrawing?.lastRevision?.editedAt" class="warning-message">
      {{ $t("lastModified") }} :
      {{ formatDistanceToNow(new Date(localDrawing.lastRevision.editedAt)) }}
    </div>
  </DRDialog>
</template>

<style lang="scss" scoped>
.error-message {
  color: var(--bs-danger);
  font-weight: 600;
  font-size: 0.875rem;
  background-color: var(--bs-danger-bg-subtle);
  padding: 0.5rem;
  border-radius: 4px;
}

.warning-message {
  color: var(--bs-warning-text-emphasis);
  font-weight: 600;
  font-size: 0.875rem;
  background-color: var(--bs-warning-bg-subtle);
  padding: 0.5rem;
  border-radius: 4px;
}
</style>
